import type { DiGraph } from "../graph/digraph.js";
import type { GraphScores } from "../graph/scores.js";
import type { GraphSelectWeights } from "../graph/graphSelect.js";
import type { DistractorMode } from "../generate/pickDistractors.js";
import type { MasteryState } from "../mastery/masteryState.js";
import type { PlayerState } from "../session.js";
import type { Question } from "../types.js";

export const GAME_MASTER_IDS = ["default", "patient", "drill"] as const;
export type GameMasterId = (typeof GAME_MASTER_IDS)[number];

/**
 * Tuning for question selection, urgency, and generated MCQ distractor shape.
 */
export type GameMasterParams = {
  targetElo: number;
  masteryBlend: number;
  preferredBand: number;
  fallbackBand: number;
  recencyWindow: number;
  showStreak: boolean;
  generatedDistractor: "standard" | "near" | "far";
  weights?: Partial<GraphSelectWeights>;
};

export type GmView = "home" | "mcq" | "summary";

/**
 * “Game master” / tutor policy: MCQ pick, round urgency target, focus recommendation,
 * and optional PWA hint kind.
 */
export type GameMaster = {
  id: GameMasterId;
  params: GameMasterParams;
  pickNextQuestion: (args: {
    bank: readonly Question[];
    player: PlayerState;
    mastery: MasteryState;
    scores: GraphScores;
    excludeIds: Set<string>;
    pass: "first" | "retry";
  }) => Question | null;
  targetEloForRounds: () => number;
  recommendConcept: (args: {
    graph: DiGraph;
    scores: GraphScores;
    mastery: MasteryState;
  }) => string | null;
  framingFor: (view: GmView) => "patient" | "drill" | null;
  /**
   * For `generateMcqFromConcept`: `undefined` = use `distractorModeForRating(rating)`.
   * Otherwise fixed mode for the whole blended bank.
   */
  distractorForGenerated: (rating: number) => DistractorMode | undefined;
};
